
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import AddFriendModal from "@/components/AddFriendModal";
import ConsolidatedFriendsTabContent from "./friends/ConsolidatedFriendsTabContent";
import { FriendWithStatus } from "@/types";

interface FriendsListProps {
  friends: FriendWithStatus[];
  pendingRequests: FriendWithStatus[];
  sentRequests: FriendWithStatus[];
  isLoading: boolean;
  sendFriendRequest: (friendId: string) => Promise<unknown>;
  acceptFriendRequest: (friendshipId: string) => Promise<void>;
  rejectFriendRequest: (friendshipId: string) => Promise<void>;
  removeFriend: (friendshipId: string) => Promise<void>;
  removeManualFriend?: (manualPlayerId: string) => Promise<void>;
  refreshFriendships: () => Promise<void>;
  onInviteManualPlayer?: (manualPlayerId: string, email: string) => Promise<void>;
  invitedPlayerIds?: Set<string>;
}

const FriendsList = ({
  friends,
  pendingRequests,
  sentRequests,
  isLoading,
  sendFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend,
  removeManualFriend,
  refreshFriendships,
  onInviteManualPlayer,
  invitedPlayerIds,
}: FriendsListProps) => {
  const [isAddFriendModalOpen, setIsAddFriendModalOpen] = useState(false);

  const handleOpenAddFriendModal = () => {
    setIsAddFriendModalOpen(true);
  };

  const handleCloseAddFriendModal = () => {
    setIsAddFriendModalOpen(false);
  };

  const totalConnections = friends.length + pendingRequests.length + sentRequests.length;

  return (
    <div className="px-4 pb-20">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-forest">Friends</h2>
          {totalConnections > 0 && (
            <Badge className="bg-forest">{totalConnections}</Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1"
          onClick={handleOpenAddFriendModal}
        >
          <PlusCircle className="h-4 w-4" />
          Add Friend
        </Button>
      </div>

      <ConsolidatedFriendsTabContent
        friends={friends}
        pendingRequests={pendingRequests}
        sentRequests={sentRequests}
        isLoading={isLoading}
        onOpenAddFriendModal={handleOpenAddFriendModal}
        onAccept={acceptFriendRequest}
        onReject={rejectFriendRequest}
        onCancel={removeFriend}
        onRemoveManualFriend={removeManualFriend}
        onInviteManualPlayer={onInviteManualPlayer}
        invitedPlayerIds={invitedPlayerIds}
      />

      <AddFriendModal
        isOpen={isAddFriendModalOpen}
        onClose={handleCloseAddFriendModal}
        friends={friends}
        pendingRequests={pendingRequests}
        sentRequests={sentRequests}
        sendFriendRequest={sendFriendRequest}
        refreshFriendships={refreshFriendships}
      />
    </div>
  );
};

export default FriendsList;
